import type { ProductToolIdEnumType } from '@forgd/contract/types'
import { AnalyticsEventEnum, ProductToolIdSchema } from '@forgd/contract/types'
import { resolveApexCookieDomain } from '@forgd/shared/utils/cookie-consent'
import posthog from 'posthog-js'

const appNames: Record<string, string> = {
  app: 'forgd-app',
  liquidity: 'forgd-liquidity',
}

function identifyUser(id: string, email: string | null) {
  posthog.identify(id, email ? { email } : undefined)
}

// posthog-js keeps its cookie/localStorage entries when switching to memory
// persistence, so on consent withdrawal they must be removed manually
function clearPostHogStorage() {
  const expiry = 'Thu, 01 Jan 1970 00:00:00 GMT'
  const apexDomain = resolveApexCookieDomain(window.location.hostname)

  document.cookie.split(';').forEach((c) => {
    const cookieName = c.trim().split('=')[0]
    if (cookieName?.startsWith('ph_')) {
      document.cookie = `${cookieName}=;expires=${expiry};path=/`
      if (apexDomain) {
        document.cookie = `${cookieName}=;expires=${expiry};path=/;domain=${apexDomain}`
      }
    }
  })
  Object.keys(localStorage)
    .filter(key => key.startsWith('ph_'))
    .forEach(key => localStorage.removeItem(key))
  Object.keys(sessionStorage)
    .filter(key => key.startsWith('ph_'))
    .forEach(key => sessionStorage.removeItem(key))
}

function hasAuthMiddleware(middleware: unknown) {
  if (typeof middleware === 'string') {
    return middleware === 'auth'
  }

  if (Array.isArray(middleware)) {
    return middleware.includes('auth')
  }

  return false
}

function resolveToolFromMeta(meta: Record<string, unknown>): ProductToolIdEnumType | undefined {
  const result = ProductToolIdSchema.safeParse(meta.productTool)
  return result.success ? result.data : undefined
}

function resolveSubToolFromMeta(meta: Record<string, unknown>): string | undefined {
  const value = meta.productSubTool
  return typeof value === 'string' ? value : undefined
}

export default defineNuxtPlugin({
  name: 'posthog',
  dependsOn: ['auth'],
  setup(nuxtApp) {
    const runtimeConfig = useRuntimeConfig()
    const { portal, posthog: posthogConfig } = runtimeConfig.public as typeof runtimeConfig.public & { portal: string }
    const appName = appNames[portal]
    const route = useRoute()
    const { capture } = useAnalytics()

    if (!appName || !posthogConfig.enabled || !posthogConfig.projectToken) {
      return
    }

    const { analyticsConsent } = useCookieConsent()

    // cookieless until analytics consent is granted; tracked locally so the consent
    // watch only calls set_config on actual changes (persistence switches make
    // posthog-js re-evaluate and migrate its storage layer)
    let persistence: 'localStorage+cookie' | 'memory'
      = analyticsConsent.value === 'granted' ? 'localStorage+cookie' : 'memory'

    posthog.init(posthogConfig.projectToken, {
      advanced_disable_flags: true,
      api_host: posthogConfig.apiHost,
      autocapture: false,
      capture_pageleave: true,
      capture_pageview: 'history_change',
      disable_session_recording: true,
      enable_heatmaps: false,
      persistence,
    })

    posthog.register({
      app_name: appName,
      environment: posthogConfig.environment,
      portal,
      product_suite: 'forgd',
    })

    let lastSessionTool: ProductToolIdEnumType | undefined
    let lastSessionProjectId: string | undefined

    const auth = useAuth()
    watch(
      () => ({
        email: auth.me?.email ?? null,
        fullPath: route.fullPath,
        id: auth.me?.id,
        isLoggedIn: auth.isLoggedIn,
        middleware: route.meta.middleware,
        projectId: auth.project?.id,
      }),
      ({ email, fullPath, id, isLoggedIn, middleware, projectId }) => {
        // consent is read, not watched — the dedicated consent watch below handles
        // identify-on-grant, so consent changes must not re-fire page-view captures
        if (isLoggedIn && id && analyticsConsent.value === 'granted') {
          identifyUser(id, email)
        }

        if (
          fullPath
          && isLoggedIn
          && id
          && hasAuthMiddleware(middleware)
        ) {
          const tool = resolveToolFromMeta(route.meta)
          const subTool = resolveSubToolFromMeta(route.meta)
          const projectProps = projectId ? { project_id: projectId } : {}
          const endedSessionProjectProps = lastSessionProjectId ? { project_id: lastSessionProjectId } : {}

          if (tool && tool !== lastSessionTool) {
            if (lastSessionTool) {
              capture(AnalyticsEventEnum.ToolSessionEnded, {
                path: fullPath,
                product_tool: lastSessionTool,
                ...endedSessionProjectProps,
              })
            }
            capture(AnalyticsEventEnum.ToolSessionStarted, {
              path: fullPath,
              product_tool: tool,
              ...(subTool ? { product_sub_tool: subTool } : {}),
              ...projectProps,
            })
            lastSessionTool = tool
            lastSessionProjectId = projectId
          }
          else if (!tool) {
            if (lastSessionTool) {
              capture(AnalyticsEventEnum.ToolSessionEnded, {
                path: fullPath,
                product_tool: lastSessionTool,
                ...endedSessionProjectProps,
              })
            }
            lastSessionTool = undefined
            lastSessionProjectId = undefined
          }

          capture(AnalyticsEventEnum.AuthenticatedPageViewed, {
            path: fullPath,
            ...(tool ? { product_tool: tool } : {}),
            ...(subTool ? { product_sub_tool: subTool } : {}),
            ...projectProps,
          })
        }
      },
      { immediate: true },
    )

    // immediate so a visitor arriving with a stored denial gets legacy-era ph_*
    // cookies cleaned at boot, not only on an in-session granted → denied transition
    watch(analyticsConsent, (consent) => {
      if (consent === 'granted') {
        if (persistence !== 'localStorage+cookie') {
          persistence = 'localStorage+cookie'
          posthog.set_config({ persistence })
        }
        if (auth.isLoggedIn && auth.me?.id) {
          identifyUser(auth.me.id, auth.me.email ?? null)
        }
      }
      else {
        // switch to memory first so posthog-js cannot re-write the cookie after cleanup;
        // 'unset' (e.g. the flag turning on mid-session) only downgrades persistence —
        // stored state is destroyed exclusively on an explicit denial
        if (persistence !== 'memory') {
          persistence = 'memory'
          posthog.set_config({ persistence })
        }
        if (consent === 'denied') {
          posthog.reset()
          clearPostHogStorage()
        }
      }
    }, { immediate: true })

    // Avoid posthog.reset() when logged out here: keep the anonymous PostHog session until
    // explicit logout (`forgd:auth:reset`).

    nuxtApp.hook('forgd:auth:reset', () => {
      if (lastSessionTool) {
        capture(AnalyticsEventEnum.ToolSessionEnded, {
          path: route.fullPath,
          product_tool: lastSessionTool,
          ...(lastSessionProjectId ? { project_id: lastSessionProjectId } : {}),
        })
      }
      lastSessionTool = undefined
      lastSessionProjectId = undefined
      posthog.reset()
    })
  },
})
